import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Trash2, Edit3, Save, X, ExternalLink, Github, 
  Sparkles, ShieldCheck, KeyRound, LogOut, Download, Code2, FolderKanban
} from 'lucide-react';
import { getProjects, saveProjects } from '../data/projectsData';
import { getSkills, saveSkills, AVAILABLE_ICONS } from '../data/skillsData';
import { getSkillIcon } from './SkillIconRenderer';

// Secret Passcode for the Dashboard
const ADMIN_PASSCODE = '20030720';

export default function AdminModal({ isOpen, onClose }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTab, setActiveTab] = useState('projects'); // 'projects' | 'skills'
  
  // Projects state
  const [projectsList, setProjectsList] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: '',
    demoLink: '',
    githubLink: '',
    image: '',
    featured: false,
  });

  // Skills state
  const [skillsList, setSkillsList] = useState([]);
  const [editingSkillIndex, setEditingSkillIndex] = useState(null);
  const [showSkillForm, setShowSkillForm] = useState(false);
  const [skillFormData, setSkillFormData] = useState({
    name: '',
    iconKey: 'FaReact',
    color: '#61dafb',
  });

  // Load data on open; AUTO-LOCK when modal closes or opens fresh!
  useEffect(() => {
    if (isOpen) {
      setProjectsList(getProjects());
      setSkillsList(getSkills());
      setPasscode('');
      setErrorMsg('');
      // Auto-lock: Requires entering passcode every time the modal opens
      setIsAuthenticated(false);
    } else {
      // When closed, reset authentication and forms immediately
      setIsAuthenticated(false);
      setPasscode('');
      setErrorMsg('');
      resetForm();
      resetSkillForm();
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAuthenticated(false);
    setPasscode('');
    setErrorMsg('');
    onClose();
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (passcode.trim() === ADMIN_PASSCODE) {
      setIsAuthenticated(true);
      setErrorMsg('');
      setPasscode('');
    } else {
      setErrorMsg('Incorrect passcode! Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPasscode('');
    onClose();
  };

  // --- Project Management Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      tags: '',
      demoLink: '',
      githubLink: '',
      image: '',
      featured: false,
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleEditClick = (proj) => {
    setEditingId(proj.id || proj.title);
    setFormData({
      title: proj.title,
      description: proj.description,
      tags: Array.isArray(proj.tags) ? proj.tags.join(', ') : proj.tags,
      demoLink: proj.links?.demo || '',
      githubLink: proj.links?.github || '',
      image: proj.image || '',
      featured: !!proj.featured,
    });
    setShowAddForm(true);
  };

  const handleSaveProject = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;

    const parsedTags = formData.tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const newProjectObj = {
      id: editingId || `proj-${Date.now()}`,
      title: formData.title.trim(),
      description: formData.description.trim(),
      tags: parsedTags.length > 0 ? parsedTags : ['React', 'JavaScript'],
      links: {
        demo: formData.demoLink.trim() || '#',
        github: formData.githubLink.trim() || '#',
      },
      image:
        formData.image.trim() ||
        'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      featured: formData.featured,
    };

    let updatedList;
    if (editingId) {
      updatedList = projectsList.map((p) =>
        (p.id === editingId || p.title === editingId ? newProjectObj : p)
      );
    } else {
      updatedList = [newProjectObj, ...projectsList];
    }

    setProjectsList(updatedList);
    saveProjects(updatedList);
    resetForm();
  };

  const handleDelete = (idOrTitle) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      const updated = projectsList.filter((p) => (p.id || p.title) !== idOrTitle);
      setProjectsList(updated);
      saveProjects(updated);
      if (editingId === idOrTitle) resetForm();
    }
  };


  const handleExportJSON = () => {
    const dataToExport = {
      projects: projectsList,
      skills: skillsList
    };
    const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(dataToExport, null, 2)
    )}`;
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', jsonString);
    downloadAnchor.setAttribute('download', 'portfolio-data.json');
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  // --- Skill Management Handlers ---
  const resetSkillForm = () => {
    setSkillFormData({
      name: '',
      iconKey: 'FaReact',
      color: '#61dafb',
    });
    setEditingSkillIndex(null);
    setShowSkillForm(false);
  };

  const handleEditSkill = (skill, index) => {
    setEditingSkillIndex(index);
    setSkillFormData({
      name: skill.name,
      iconKey: skill.iconKey || 'FaCode',
      color: skill.color || '#61dafb',
    });
    setShowSkillForm(true);
  };

  const handleIconSelect = (key) => {
    const found = AVAILABLE_ICONS.find((item) => item.key === key);
    setSkillFormData((prev) => ({
      ...prev,
      iconKey: key,
      color: found?.defaultColor || prev.color,
    }));
  };

  const handleSaveSkill = (e) => {
    e.preventDefault();
    if (!skillFormData.name.trim()) return;

    const newSkill = {
      name: skillFormData.name.trim(),
      iconKey: skillFormData.iconKey,
      color: skillFormData.color,
    };

    let updatedSkills;
    if (editingSkillIndex !== null) {
      updatedSkills = [...skillsList];
      updatedSkills[editingSkillIndex] = newSkill;
    } else {
      updatedSkills = [...skillsList, newSkill];
    }

    setSkillsList(updatedSkills);
    saveSkills(updatedSkills);
    resetSkillForm();
  };

  const handleDeleteSkill = (index) => {
    if (window.confirm('Are you sure you want to delete this skill?')) {
      const updated = skillsList.filter((_, idx) => idx !== index);
      setSkillsList(updated);
      saveSkills(updated);
      if (editingSkillIndex === index) resetSkillForm();
    }
  };


  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-4xl max-h-[92vh] flex flex-col bg-[#1b2d48] border border-[#4779c4]/40 rounded-2xl shadow-2xl overflow-hidden text-slate-100"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-[#2c456b]/80 via-[#1b2d48] to-[#1b2d48]">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#4779c4]/20 text-[#38bdf8] rounded-xl border border-[#4779c4]/30">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-wide text-white flex items-center gap-2">
                  Admin Management Portal
                  <span className="text-[11px] bg-emerald-500/20 text-emerald-300 font-mono px-2 py-0.5 rounded-full border border-emerald-500/30">
                    Auto-Lock Protected
                  </span>
                </h2>
                <p className="text-xs text-slate-400">
                  Manage your portfolio projects & skills securely in real-time
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          {!isAuthenticated ? (
            /* Login Gate */
            <div className="p-8 sm:p-12 flex flex-col items-center justify-center text-center my-auto">
              <div className="w-16 h-16 bg-blue-600/20 border border-blue-500/30 rounded-full flex items-center justify-center mb-5 text-blue-400 shadow-inner">
                <KeyRound className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold mb-2">Restricted Access</h3>
              <p className="text-sm text-slate-400 max-w-sm mb-6">
                Enter your secret password to unlock the admin dashboard. Auto-locks automatically upon closing.
              </p>

              <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4">
                <div>
                  <input
                    type="password"
                    placeholder="Enter Secret Passcode"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    autoFocus
                    className="w-full px-4 py-3 bg-slate-900/90 border border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 text-white placeholder-slate-500 text-center tracking-wider text-sm transition-all"
                  />
                  {errorMsg && (
                    <p className="text-rose-400 text-xs mt-2 font-medium">{errorMsg}</p>
                  )}
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl transition-all shadow-lg shadow-blue-600/30 active:scale-[0.98]"
                >
                  Unlock Dashboard
                </button>
              </form>
            </div>
          ) : (
            /* Authenticated Admin Dashboard */
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 flex flex-col">
              {/* Tab Navigation */}
              <div className="flex items-center justify-between border-b border-white/10 pb-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveTab('projects')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      activeTab === 'projects'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                        : 'bg-slate-800/80 text-slate-400 hover:text-white'
                    }`}
                  >
                    <FolderKanban className="w-4 h-4" />
                    Projects ({projectsList.length})
                  </button>

                  <button
                    onClick={() => setActiveTab('skills')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                      activeTab === 'skills'
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                        : 'bg-slate-800/80 text-slate-400 hover:text-white'
                    }`}
                  >
                    <Code2 className="w-4 h-4" />
                    Skills ({skillsList.length})
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExportJSON}
                    title="Export all data as JSON"
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs transition-all border border-slate-700"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Export JSON</span>
                  </button>

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-rose-400 hover:bg-rose-500/10 rounded-lg border border-rose-500/20 transition-colors"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Lock & Exit
                  </button>
                </div>
              </div>

              {/* ==================== PROJECTS TAB ==================== */}
              {activeTab === 'projects' && (
                <div className="space-y-6">
                  {/* Action Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/60 p-3 rounded-xl border border-white/5">
                    <button
                      onClick={() => {
                        resetForm();
                        setShowAddForm(!showAddForm);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all shadow-md active:scale-95"
                    >
                      <Plus className="w-4 h-4" />
                      {showAddForm ? 'Hide Form' : 'Add New Project'}
                    </button>

                  </div>

                  {/* Add / Edit Form Collapsible */}
                  <AnimatePresence>
                    {showAddForm && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <form
                          onSubmit={handleSaveProject}
                          className="bg-slate-900/90 border border-blue-500/30 rounded-xl p-5 space-y-4"
                        >
                          <div className="flex items-center justify-between border-b border-white/5 pb-2">
                            <h4 className="font-semibold text-sm text-blue-400 flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              {editingId ? 'Edit Project' : 'Add New Project'}
                            </h4>
                            <button
                              type="button"
                              onClick={resetForm}
                              className="text-xs text-slate-400 hover:text-white"
                            >
                              Cancel
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Project Title *
                              </label>
                              <input
                                type="text"
                                required
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="e.g. AI Content Generator"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Tech Stack Tags (comma separated)
                              </label>
                              <input
                                type="text"
                                name="tags"
                                value={formData.tags}
                                onChange={handleInputChange}
                                placeholder="React, Tailwind, Node.js, Firebase"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Description
                              </label>
                              <textarea
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Write a brief overview of the project and your role..."
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white resize-none"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Live Demo URL
                              </label>
                              <input
                                type="text"
                                name="demoLink"
                                value={formData.demoLink}
                                onChange={handleInputChange}
                                placeholder="https://myproject.com or #"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                GitHub Repository URL
                              </label>
                              <input
                                type="text"
                                name="githubLink"
                                value={formData.githubLink}
                                onChange={handleInputChange}
                                placeholder="https://github.com/user/repo or #"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div className="sm:col-span-2">
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Image URL (Unsplash or direct image link)
                              </label>
                              <input
                                type="text"
                                name="image"
                                value={formData.image}
                                onChange={handleInputChange}
                                placeholder="https://images.unsplash.com/..."
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div className="flex items-center gap-2 sm:col-span-2">
                              <input
                                type="checkbox"
                                id="featuredCheck"
                                name="featured"
                                checked={formData.featured}
                                onChange={handleInputChange}
                                className="w-4 h-4 rounded text-blue-600 bg-slate-800 border-slate-700"
                              />
                              <label htmlFor="featuredCheck" className="text-xs text-slate-300 cursor-pointer">
                                Feature this project (Displays highlight badge on the card)
                              </label>
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                            <button
                              type="button"
                              onClick={resetForm}
                              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-all shadow-md active:scale-95"
                            >
                              <Save className="w-3.5 h-3.5" />
                              {editingId ? 'Update Project' : 'Save Project'}
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Projects List View */}
                  <div className="space-y-3">
                    <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      Active Projects in Portfolio ({projectsList.length})
                    </h4>

                    {projectsList.length === 0 ? (
                      <div className="p-8 text-center border border-dashed border-slate-800 rounded-xl text-slate-500 text-sm">
                        No projects found. Click "Add New Project" above to create one!
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {projectsList.map((proj) => {
                          const id = proj.id || proj.title;
                          return (
                            <div
                              key={id}
                              className="flex flex-col justify-between p-4 bg-slate-900/70 border border-slate-800 hover:border-slate-700 rounded-xl transition-all"
                            >
                              <div className="flex items-start gap-3">
                                <img
                                  src={proj.image}
                                  alt={proj.title}
                                  className="w-16 h-16 rounded-lg object-cover border border-white/10 shrink-0 bg-slate-800"
                                  onError={(e) => {
                                    e.target.src =
                                      'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80';
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h5 className="font-semibold text-sm text-white truncate">
                                      {proj.title}
                                    </h5>
                                    {proj.featured && (
                                      <span className="text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded border border-amber-500/30 font-medium">
                                        Featured
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 line-clamp-2 mt-1">
                                    {proj.description}
                                  </p>
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {proj.tags?.slice(0, 3).map((tag, i) => (
                                      <span
                                        key={i}
                                        className="text-[10px] px-1.5 py-0.5 bg-slate-800 text-slate-300 rounded"
                                      >
                                        {tag}
                                      </span>
                                    ))}
                                    {proj.tags?.length > 3 && (
                                      <span className="text-[10px] text-slate-500">
                                        +{proj.tags.length - 3}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/5">
                                <div className="flex items-center gap-2">
                                  {proj.links?.demo && proj.links.demo !== '#' && (
                                    <a
                                      href={proj.links.demo}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-slate-400 hover:text-blue-400 flex items-center gap-1"
                                    >
                                      <ExternalLink className="w-3 h-3" /> Live
                                    </a>
                                  )}
                                  {proj.links?.github && proj.links.github !== '#' && (
                                    <a
                                      href={proj.links.github}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-slate-400 hover:text-blue-400 flex items-center gap-1"
                                    >
                                      <Github className="w-3 h-3" /> Repo
                                    </a>
                                  )}
                                </div>

                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => handleEditClick(proj)}
                                    className="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                    title="Edit project"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(id)}
                                    className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                    title="Delete project"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ==================== SKILLS TAB ==================== */}
              {activeTab === 'skills' && (
                <div className="space-y-6">
                  {/* Skills Action Bar */}
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-900/60 p-3 rounded-xl border border-white/5">
                    <button
                      onClick={() => {
                        resetSkillForm();
                        setShowSkillForm(!showSkillForm);
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all shadow-md active:scale-95"
                    >
                      <Plus className="w-4 h-4" />
                      {showSkillForm ? 'Hide Form' : 'Add New Skill'}
                    </button>

                  </div>

                  {/* Add / Edit Skill Form */}
                  <AnimatePresence>
                    {showSkillForm && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                      >
                        <form
                          onSubmit={handleSaveSkill}
                          className="bg-slate-900/90 border border-blue-500/30 rounded-xl p-5 space-y-4"
                        >
                          <div className="flex items-center justify-between border-b border-white/5 pb-2">
                            <h4 className="font-semibold text-sm text-blue-400 flex items-center gap-2">
                              <Sparkles className="w-4 h-4" />
                              {editingSkillIndex !== null ? 'Edit Skill' : 'Add New Skill'}
                            </h4>
                            <button
                              type="button"
                              onClick={resetSkillForm}
                              className="text-xs text-slate-400 hover:text-white"
                            >
                              Cancel
                            </button>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Skill Name *
                              </label>
                              <input
                                type="text"
                                required
                                value={skillFormData.name}
                                onChange={(e) =>
                                  setSkillFormData({ ...skillFormData, name: e.target.value })
                                }
                                placeholder="e.g. Next.js, Docker, Java"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Choose Icon
                              </label>
                              <select
                                value={skillFormData.iconKey}
                                onChange={(e) => handleIconSelect(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white"
                              >
                                {AVAILABLE_ICONS.map((icon) => (
                                  <option key={icon.key} value={icon.key}>
                                    {icon.label} ({icon.key})
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs font-medium text-slate-300 mb-1">
                                Brand / Accent Color
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={skillFormData.color}
                                  onChange={(e) =>
                                    setSkillFormData({ ...skillFormData, color: e.target.value })
                                  }
                                  className="w-10 h-9 bg-transparent border-0 cursor-pointer rounded"
                                />
                                <input
                                  type="text"
                                  value={skillFormData.color}
                                  onChange={(e) =>
                                    setSkillFormData({ ...skillFormData, color: e.target.value })
                                  }
                                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 text-white uppercase text-xs"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Live Preview */}
                          <div className="p-3 bg-slate-800/50 rounded-xl border border-white/5 flex items-center justify-between">
                            <span className="text-xs text-slate-400">Preview:</span>
                            <div className="flex items-center gap-2.5">
                              <span className="text-2xl">
                                {getSkillIcon(skillFormData.iconKey, skillFormData.color)}
                              </span>
                              <span className="text-sm font-semibold text-white">
                                {skillFormData.name || 'Skill Name'}
                              </span>
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
                            <button
                              type="button"
                              onClick={resetSkillForm}
                              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs rounded-lg transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-lg transition-all shadow-md active:scale-95"
                            >
                              <Save className="w-3.5 h-3.5" />
                              {editingSkillIndex !== null ? 'Update Skill' : 'Save Skill'}
                            </button>
                          </div>
                        </form>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Skills Grid in Admin */}
                  <div className="space-y-3">
                    <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                      Current Skills Toolbox ({skillsList.length})
                    </h4>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {skillsList.map((skill, index) => (
                        <div
                          key={skill.name + index}
                          className="flex flex-col items-center justify-between p-3.5 bg-slate-900/70 border border-slate-800 rounded-xl hover:border-slate-700 group transition-all"
                        >
                          <div className="text-3xl my-2">
                            {getSkillIcon(skill.iconKey, skill.color)}
                          </div>

                          <span className="text-xs font-semibold text-slate-200 text-center mb-2 truncate w-full">
                            {skill.name}
                          </span>

                          <div className="flex items-center gap-1 pt-2 border-t border-white/5 w-full justify-center">
                            <button
                              onClick={() => handleEditSkill(skill, index)}
                              className="p-1 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
                              title="Edit skill"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteSkill(index)}
                              className="p-1 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded transition-colors"
                              title="Delete skill"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
